import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import CreateUserModal from '../components/CreateUserModal';
import CreateProjectWizard from '../components/CreateProjectWizard';
import EditProjectModal from '../components/EditProjectModal';
import ProjectActionsMenu from '../components/ProjectActionsMenu';
import ProjectApprovalModal from '../components/ProjectApprovalModal';
import UserManagement from '../components/UserManagement';
import PendingMilestoneReviews from '../components/PendingMilestoneReviews';
import { api } from '../utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Users, Building2, Plus, IndianRupee, UserPlus, ClipboardCheck, AlertCircle, Settings, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, projectsRes, customersRes, engineersRes, pendingRes, allUsersRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/projects'),
        api.get('/users?role=customer'),
        api.get('/users?role=engineer'),
        api.get('/projects/pending-approval'),
        api.get('/users')
      ]);
      
      setUser(userRes.data);
      // Filter out pending projects from main list
      setProjects(projectsRes.data.filter(p => !['pending_approval', 'in_review'].includes(p.status)));
      setPendingProjects(pendingRes.data);
      setCustomers(customersRes.data);
      setEngineers(engineersRes.data);
      setAllUsers(allUsersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowEditProject(true);
  };

  const handleApproveProject = (project) => {
    setSelectedProject(project);
    setShowApprovalModal(true);
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'on_hold':
        return 'bg-orange-100 text-orange-700';
      case 'pending_approval':
        return 'bg-amber-100 text-amber-700';
      case 'in_review':
        return 'bg-purple-100 text-purple-700';
      case 'active':
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'on_hold':
        return 'On Hold';
      case 'pending_approval':
        return 'Pending Approval';
      case 'in_review':
        return 'In Review';
      case 'active':
      default:
        return 'Active';
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalRevenue = projects.reduce((sum, p) => sum + (p.contract_value || 0), 0);

  return (
    <div className="min-h-screen bg-background transition-colors duration-300" data-testid="admin-dashboard">
      <DashboardHeader user={user} unreadCount={0} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-1">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">Manage all projects and teams</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              onClick={() => setShowCreateUser(true)}
              variant="outline"
              className="border-border text-foreground hover:bg-muted rounded-full touch-target"
              data-testid="create-user-button"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              <span className="hidden sm:inline">Add User</span>
            </Button>
            <Button 
              onClick={() => setShowCreateProject(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full touch-target"
              data-testid="create-project-button"
            >
              <Plus className="mr-2 h-5 w-5" />
              <span className="hidden sm:inline">New Project</span>
            </Button>
          </div>
        </motion.div>

        {/* Stats Grid - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
          {[
            { label: 'Projects', value: projects.length, icon: Building2, color: 'text-primary' },
            { label: 'Active', value: projects.filter(p => p.status === 'active').length, icon: Calendar, color: 'text-blue-500' },
            { label: 'Pending', value: pendingProjects.length, icon: ClipboardCheck, color: 'text-amber-500' },
            { label: 'Users', value: allUsers.length, icon: Users, color: 'text-green-500' },
            { label: 'Revenue', value: `₹${(totalRevenue / 10000000).toFixed(1)}Cr`, icon: IndianRupee, color: 'text-cyan-500' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="stat-card border-border bg-card hover:shadow-lg transition-all duration-300">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-xl sm:text-3xl font-bold text-foreground">{stat.value}</p>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-xl bg-muted ${stat.color}`}>
                      <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-stone-200 p-1">
            <TabsTrigger value="projects" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
              <Building2 className="h-4 w-4 mr-2" />
              Projects
            </TabsTrigger>
            <TabsTrigger value="milestones" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
              <Target className="h-4 w-4 mr-2" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="space-y-6">
            {/* Pending Approvals Section */}
            {pendingProjects.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50 rounded-lg shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="h-6 w-6 text-amber-600" />
                      <div>
                        <CardTitle className="text-xl font-semibold text-amber-900">Pending Approvals</CardTitle>
                        <CardDescription className="text-amber-700">{pendingProjects.length} project(s) awaiting your review</CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingProjects.map((project, index) => (
                      <motion.div
                        key={project.project_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div 
                          className="border border-amber-200 rounded-lg p-5 bg-white hover:shadow-md transition-all cursor-pointer"
                          onClick={() => handleApproveProject(project)}
                          data-testid={`pending-project-${project.project_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <h3 className="text-lg font-semibold text-stone-900">
                                  {project.project_name}
                                </h3>
                                <Badge className={`${getStatusBadgeStyle(project.status)} rounded-full px-3`}>
                                  {project.status === 'pending_approval' ? 'Customer Request' : 'Engineer Submission'}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-4 gap-6 text-sm">
                                <div>
                                  <p className="text-stone-500 mb-1">Customer</p>
                                  <p className="font-medium text-stone-900">{project.customer_name}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Location</p>
                                  <p className="font-medium text-stone-900">{project.location}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Built-up Area</p>
                                  <p className="font-medium text-stone-900">{project.built_up_area?.toLocaleString('en-IN') || 'N/A'} sq.ft</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Submitted On</p>
                                  <p className="font-medium text-stone-900">
                                    {new Date(project.created_at).toLocaleDateString('en-IN')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <Button className="ml-4 bg-amber-600 hover:bg-amber-700 text-white">
                              Review
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Projects Table */}
            <Card className="border-stone-200 rounded-lg shadow-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold">All Projects</CardTitle>
                <CardDescription>Manage and monitor all construction projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.length === 0 ? (
                    <div className="text-center py-12">
                      <Building2 className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-600">No projects yet. Create your first project.</p>
                    </div>
                  ) : (
                    projects.map((project, index) => (
                      <motion.div
                        key={project.project_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div 
                          className="border border-stone-200 rounded-lg p-5 hover:shadow-md transition-all bg-white"
                          data-testid={`admin-project-${project.project_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <Link to={`/projects/${project.project_id}`} className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <h3 className="text-lg font-semibold text-stone-900 hover:text-stone-700 transition-colors">
                                  {project.project_name}
                                </h3>
                                <Badge className={`${getStatusBadgeStyle(project.status)} hover:${getStatusBadgeStyle(project.status)} rounded-full px-3`}>
                                  {getStatusLabel(project.status)}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-4 gap-6 text-sm">
                                <div>
                                  <p className="text-stone-500 mb-1">Customer</p>
                                  <p className="font-medium text-stone-900">{project.customer_name}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Location</p>
                                  <p className="font-medium text-stone-900">{project.location}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Contract Value</p>
                                  <p className="font-medium text-stone-900">
                                    {project.contract_value ? `₹${project.contract_value.toLocaleString('en-IN')}` : 'TBD'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Completion</p>
                                  <p className="font-medium text-stone-900">
                                    {project.expected_completion_date ? new Date(project.expected_completion_date).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    }) : 'TBD'}
                                  </p>
                                </div>
                              </div>
                            </Link>
                            
                            {/* 3-dot Menu */}
                            <ProjectActionsMenu 
                              project={project} 
                              onEdit={handleEditProject}
                              onRefresh={fetchData}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="milestones" className="space-y-6">
            <Card className="border-stone-200 rounded-lg shadow-sm">
              <CardHeader>
                <div>
                  <CardTitle className="text-2xl font-semibold">Milestone Reviews</CardTitle>
                  <CardDescription>Review and approve milestone completions submitted by engineers</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <PendingMilestoneReviews onUpdate={fetchData} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card className="border-stone-200 rounded-lg shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-semibold">User Management</CardTitle>
                    <CardDescription>View and manage all users in the system</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowCreateUser(true)}
                    className="bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {allUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No users yet.</p>
                  </div>
                ) : (
                  <UserManagement users={allUsers} onRefresh={fetchData} />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <CreateUserModal
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onSuccess={fetchData}
      />

      <CreateProjectWizard
        open={showCreateProject}
        onClose={() => setShowCreateProject(false)}
        onSuccess={fetchData}
        customers={customers}
        engineers={engineers}
      />

      <EditProjectModal
        project={selectedProject}
        open={showEditProject}
        onClose={() => {
          setShowEditProject(false);
          setSelectedProject(null);
        }}
        onSuccess={fetchData}
        customers={customers}
        engineers={engineers}
      />

      <ProjectApprovalModal
        open={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default AdminDashboard;
